import React, { useMemo } from 'react';
import { Table } from 'reactstrap';

import { formatCurrency } from '~libs/dataUtils';

import TableEmpty from '~components/table-view-v3/TableEmpty';
import TableLoading from '~components/table-view-v3/TableLoading';

import { StatementBudgetsSummaryRow } from '~support-at-home/models/dashboard';

interface ComponentProps {
  rows: StatementBudgetsSummaryRow[];
  isLoading: boolean;
  visibleFundingTypes?: string[] | null;
}

const StatementBudgetsTable: React.FC<ComponentProps> = ({ rows, isLoading, visibleFundingTypes }) => {
  const budgetTypeLabels = useMemo(() => {
    const labels = new Set<string>();
    rows.forEach((row) => {
      row.budgets.forEach((budget) => {
        if (budget.budget_type_label) labels.add(budget.budget_type_label);
      });
    });
    const allLabels = Array.from(labels).sort();
    if (visibleFundingTypes && visibleFundingTypes.length > 0) {
      return allLabels.filter((label) => visibleFundingTypes.includes(label));
    }
    return allLabels;
  }, [rows, visibleFundingTypes]);

  // 1 (Client) + 1 (Statement) + 3 per budget type + 1 (Co-Contributions)
  const totalColSpan = 2 + budgetTypeLabels.length * 3 + 1;

  return (
    <div className="table-view-v3 statement-budget-table">
      <Table size="sm" responsive striped bordered data-cy="statement-budget-table">
        <thead>
          <tr>
            <th rowSpan={2} className="align-middle">Client</th>
            <th rowSpan={2} className="align-middle">Statement</th>
            {budgetTypeLabels.map((label) => (
              <th key={label} colSpan={3} className="text-center group-border">
                {label}
              </th>
            ))}
            <th rowSpan={2} className="text-center align-middle group-border">
              Co-Contributions
            </th>
          </tr>
          <tr>
            {budgetTypeLabels.map((label) => (
              <React.Fragment key={label}>
                <th className="text-end group-border">Opening</th>
                <th className="text-end">Spent</th>
                <th className="text-end">Closing</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && <TableLoading colSpan={totalColSpan} />}
          {!isLoading && rows.length === 0 && (
            <TableEmpty colSpan={totalColSpan} message="No statement data found for the selected month" />
          )}
          {!isLoading &&
            rows.map((row) => (
              <tr key={row.statement_id} data-cy={`statement-budget-row-${row.statement_id}`}>
                <td>{row.client_name}</td>
                <td>{row.statement_reference}</td>
                {budgetTypeLabels.map((label) => {
                  const entry = row.budgets.find((b) => b.budget_type_label === label);
                  return (
                    <React.Fragment key={label}>
                      <td className="text-end group-border">
                        {entry ? formatCurrency(entry.opening_balance) : '--'}
                      </td>
                      <td className="text-end">{entry ? formatCurrency(entry.spent) : '--'}</td>
                      <td className="text-end">{entry ? formatCurrency(entry.closing_balance) : '--'}</td>
                    </React.Fragment>
                  );
                })}
                <td className="text-end group-border">{formatCurrency(row.co_contributions)}</td>
              </tr>
            ))}
        </tbody>
      </Table>
    </div>
  );
};

export default StatementBudgetsTable;
